import React, { useEffect, useRef, useState } from "react";
import {Link, NavLink, useNavigate, useParams} from "react-router-dom";
import api from "../api/api";

import "../styles/Product.css";
import Header from "./Components/Header";
import FooterWithSignup from "./Components/FooterWithSignup";
import { IMAGE_URL } from "../path";
import RecipeCarousel from "./Components/RecipeCarousel";
import {decode} from "html-entities";
import cartonBackground from "../assets/images/carton-background.jpg";
import SEO from "./Components/SEO";

function Product() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [page, setPage] = useState<any>(null);
    const [rangeDropdownOpen, setRangeDropdownOpen] = useState(false);
    const rangeDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                rangeDropdownRef.current &&
                !rangeDropdownRef.current.contains(event.target as Node)
            ) {
                setRangeDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    async function fetchProduct() {
        try {
            const response = await api.get(`/fetch-product/${slug}`);
            setPage(response.data);
        } catch (error) {
            console.error(error);
        }
    }


    useEffect(() => {
        if (slug) {
            fetchProduct();
        }
    }, [slug]);

    const gallery = page?.gallery ?? [];

    const currentProduct = page?.product;

    const whereToBuy = page?.where_to_buy ?? [];

    function previousSlide() {
        if (gallery.length <= 1) return;

        const currentIndex = gallery.findIndex(
            (product: any) => product.slug === slug
        );

        if (currentIndex === -1) return;

        const previousIndex =
            currentIndex === 0
                ? gallery.length - 1
                : currentIndex - 1;

        navigate(`/product/${gallery[previousIndex].slug}`);
    }

    function nextSlide() {
        if (gallery.length <= 1) return;

        const currentIndex = gallery.findIndex(
            (product: any) => product.slug === slug
        );

        if (currentIndex === -1) return;

        const nextIndex =
            currentIndex === gallery.length - 1
                ? 0
                : currentIndex + 1;

        navigate(`/product/${gallery[nextIndex].slug}`);
    }



    return (
        <div className="product-page">
            <SEO
                title={
                    currentProduct
                        ? `${currentProduct.name}${currentProduct.flavour ? ` ${currentProduct.flavour}` : ""} | Denny South Africa`
                        : "Products | DENNY"
                }
                description={
                    currentProduct?.description
                        ? decode(decode(currentProduct.description))
                            .replace(/<[^>]*>/g, "")
                            .replace(/\s+/g, " ")
                            .trim()
                        : "Discover quality Denny products and find inspiration for your next meal."
                }
                keywords={
                    currentProduct
                        ? [
                            currentProduct.name,
                            currentProduct.flavour,
                            page?.range?.display_name,
                            "Denny",
                            "Denny South Africa",
                            "recipes",
                            "cooking inspiration",
                            "meal ideas",
                        ]
                            .filter(Boolean)
                            .join(", ")
                        : "Denny products, recipes, cooking inspiration, meal ideas, South Africa"
                }
            />
            <div
                className="single-product-hero-section"
                style={{
                    backgroundImage: page?.range.header_image
                        ? `url(${IMAGE_URL}/${page?.range.header_image})`
                        : `url(${cartonBackground})`,
                }}
            >
                <Header />

                <h1>{page?.range?.name}</h1>

                <p>
                    {`${currentProduct?.flavour ?? ""} ${
                        currentProduct?.name ?? ""
                    }`}
                </p>

                <div className="product-actions">
                    <div className="product-start-cooking-button">
                        <Link className="nav-cooking" to="/lets-cook/by-cuisine">
                            Start Cooking
                        </Link>
                    </div>
                    <div className="product-nav-buttons">
                        {gallery.length > 1 && (
                            <div className="gallery-navigation">
                                <div onClick={previousSlide} className="gallery-nav-arrow">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                                        <g id="Group_918" data-name="Group 918" transform="translate(-792 -942)">
                                            <circle id="Ellipse_57" data-name="Ellipse 57" cx="12.5" cy="12.5" r="12.5" transform="translate(792 942)" fill="#436ab3"/>
                                            <path id="Path_14050" data-name="Path 14050" d="M13.25,4.5l-5,5,5,5" transform="translate(792.75 945)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                                        </g>
                                    </svg>
                                </div>

                                {gallery.map((product: any) => (
                                    <div
                                        key={product.id}
                                        className={`navigation-dot ${
                                            product.slug === currentProduct?.slug
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            navigate(`/product/${product.slug}`)
                                        }
                                    />
                                ))}

                                <div onClick={nextSlide} className="gallery-nav-arrow">
                                    <svg id="Group_917" data-name="Group 917" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                                        <circle id="Ellipse_57" data-name="Ellipse 57" cx="12.5" cy="12.5" r="12.5" fill="#436ab3"/>
                                        <path id="Path_14050" data-name="Path 14050" d="M8.25,4.5l5,5-5,5" transform="translate(2.75 3)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                </div>


            </div>

            <div className="product-info-section">
                <div className="product-info-range-buttons">
                    {page?.ranges?.map((item: any) => {
                        const isActive = item.slug === page?.range?.slug;

                        return (
                            <NavLink
                                key={item.id}
                                to={`/products/${item.slug}`}
                                className={`range-button${isActive ? " active" : ""}`}
                            >
                                {item.display_name}
                            </NavLink>
                        );
                    })}
                </div>

                <div className="product-info-ranges-dropdown" ref={rangeDropdownRef}>
                    <button
                        type="button"
                        className={`product-info-ranges-dropdown-trigger ${
                            rangeDropdownOpen ? "open" : ""
                        }`}
                        onClick={() => setRangeDropdownOpen(!rangeDropdownOpen)}
                    >
                        <span>{page?.range?.display_name}</span>

                        <svg
                            width="14"
                            height="8"
                            viewBox="0 0 14 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M1 1L7 7L13 1"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    {rangeDropdownOpen && (
                        <div className="product-info-ranges-dropdown-menu">
                            {page?.ranges?.map((item: any) => {
                                const isActive = item.slug === page?.range?.slug;

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`product-info-ranges-dropdown-option ${
                                            isActive ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setRangeDropdownOpen(false);
                                            navigate(`/products/${item.slug}`);
                                        }}
                                    >
                                        {item.display_name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="product-info-description">
                    <div className="product-info-description-image-box">
                        <div className="product-info-description-image">
                            <div className="product-info-main-image">
                                <img  src={`${IMAGE_URL}${currentProduct?.product_image}`} alt={`${currentProduct?.name ?? ""}`} />
                            </div>
                            <div className="product-info-shelf-image">
                                <img src="/wooden-platform-03.png" alt="Shelf"/>
                            </div>
                        </div>
                        <div className="where-to-buy-section-desktop">
                            <h2>Where to Purchase</h2>
                            <div className="where-to-buy-section-inner">
                                {whereToBuy.map((retailer: any) => (
                                    <div className="retailer-box" onClick={() => window.open(retailer.link, "_blank", "noopener,noreferrer")}>
                                        <img src={`${IMAGE_URL}${retailer.logo}`} alt={retailer.retailer_name} />
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                    <div className="product-info-description-details">
                        <div className="product-info-details">
                            <h2 className="prod-h2">{currentProduct?.name}</h2>
                            <h1 className="prod-h1">{currentProduct?.flavour}</h1>
                        </div>
                        <div className="product-info-description-body">
                            <div dangerouslySetInnerHTML={{ __html: decode(decode(currentProduct?.description)) }} />
                        </div>
                        <p>
                            Sizes Available:<br />
                            {currentProduct?.sizes_available?.map((size: string, index: number) => (
                                <React.Fragment key={size}>
                                    <strong>{size}</strong>
                                    {index < currentProduct.sizes_available.length - 1 && ", "}
                                </React.Fragment>
                            ))}
                        </p>
                    </div>
                    <div className="where-to-buy-section-mobile">
                        <h2>Where to Purchase</h2>
                        <div className="where-to-buy-section-inner">
                            {whereToBuy.map((retailer: any) => (
                                <div className="retailer-box" onClick={() => window.open(retailer.link, "_blank", "noopener,noreferrer")}>
                                    <img src={`${IMAGE_URL}${retailer.logo}`} alt={retailer.retailer_name} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {currentProduct?.best_served_with?.length > 0 && (
                <div className="best-served-with-section">
                    <h1>Best Served With</h1>

                    <div className="icon-section">
                        {currentProduct.best_served_with.map((bestServed: any, i: number) => (
                            <div className="icon-info" key={i}>
                                <div className="icon">
                                    <img
                                        src={`${IMAGE_URL}${bestServed.icon}`}
                                        alt={bestServed.name}
                                    />
                                </div>

                                <div className="info">{bestServed.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {page?.recipes?.length > 0 && (
                <div className="product-recipe-section">
                    <h1>Let's Cook</h1>
                    <h2>Quick & Easy-to-Prep meals</h2>

                    <RecipeCarousel recipes={page.recipes} />
                </div>
            )}

            <FooterWithSignup />
        </div>
    );
}

export default Product;
